import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, verdictStyle, timeAgo } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Link2,
  Mail,
  FileText,
  Sparkles,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  ListChecks,
} from "lucide-react";

const KINDS = [
  { key: "url", label: "URL", icon: Link2, placeholder: "https://suspicious-login.example.com/verify" },
  { key: "email", label: "Email", icon: Mail, placeholder: "Paste the full email content here..." },
  { key: "text", label: "Message", icon: FileText, placeholder: "Paste an SMS or message to analyze..." },
];

function RiskMeter({ score, verdict }) {
  const vs = verdictStyle(verdict);
  const color = verdict === "safe" ? "#00FF41" : verdict === "malicious" ? "#FF003C" : "#FFD700";
  return (
    <div>
      <div className="flex items-center justify-between font-mono text-xs mb-2">
        <span className="text-muted-foreground uppercase tracking-widest">Risk Score</span>
        <span className={vs.color}>{score}/100</span>
      </div>
      <div className="h-2 bg-secondary border border-border">
        <div className="h-full transition-[width] duration-700" style={{ width: `${score}%`, background: color, boxShadow: `0 0 8px ${color}` }} />
      </div>
    </div>
  );
}

export default function PhishingChecker() {
  const [kind, setKind] = useState("url");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/phishing/history").then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []);

  const analyze = async () => {
    if (!content.trim()) {
      toast.error("Enter something to analyze");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/phishing/check", { kind, content });
      setResult(data);
      loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const active = KINDS.find((k) => k.key === kind);
  const vs = result ? verdictStyle(result.verdict) : null;

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <PageHeader
        tag="AI Threat Intelligence"
        title="Phishing Checker"
        subtitle="Paste a URL, email or message. Our AI engine grades the risk and explains every warning sign it detects."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-card border border-border p-6">
          <div className="flex gap-2 mb-5">
            {KINDS.map((k) => {
              const Icon = k.icon;
              const a = kind === k.key;
              return (
                <button
                  key={k.key}
                  data-testid={`kind-${k.key}`}
                  onClick={() => setKind(k.key)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 border font-mono text-xs uppercase tracking-widest transition-colors duration-200 ${
                    a ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  <Icon size={14} strokeWidth={1.5} /> {k.label}
                </button>
              );
            })}
          </div>

          <Textarea
            data-testid="phishing-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={active.placeholder}
            rows={kind === "url" ? 3 : 8}
            className="rounded-none bg-input border-border font-mono text-sm resize-none focus-visible:ring-primary focus-visible:ring-2"
          />

          <Button
            data-testid="analyze-button"
            onClick={analyze}
            disabled={loading}
            className="w-full mt-4 rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Sparkles size={16} strokeWidth={1.5} className="mr-2" />}
            {loading ? "Analyzing with AI..." : "Analyze threat"}
          </Button>
        </div>

        {/* Result */}
        <div className="bg-card border border-border p-6 min-h-[280px]">
          {!result && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
              <Sparkles size={28} strokeWidth={1.5} className="text-primary/40 mb-3" />
              <p className="font-mono text-sm">Analysis results will appear here.</p>
            </div>
          )}
          {loading && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Loader2 size={28} className="animate-spin text-primary mb-3" />
              <p className="font-mono text-sm text-muted-foreground">AI engine inspecting artifact...</p>
            </div>
          )}
          {result && (
            <div data-testid="phishing-result" className="animate-fade-up space-y-5">
              <div className={`flex items-center gap-3 p-4 border ${vs.border} ${vs.bg}`}>
                {result.verdict === "safe" ? (
                  <ShieldCheck size={26} strokeWidth={1.5} className={vs.color} />
                ) : result.verdict === "malicious" ? (
                  <ShieldAlert size={26} strokeWidth={1.5} className={vs.color} />
                ) : (
                  <AlertTriangle size={26} strokeWidth={1.5} className={vs.color} />
                )}
                <div>
                  <div className={`font-mono font-bold text-lg ${vs.color}`} data-testid="phishing-verdict">{vs.label}</div>
                  <div className="text-xs text-muted-foreground">{result.summary}</div>
                </div>
              </div>

              <RiskMeter score={result.risk_score} verdict={result.verdict} />

              {result.reputation && (
                <div data-testid="reputation-block" className="border border-border bg-secondary/40 p-3">
                  <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    Reputation intel
                  </div>
                  {!result.reputation.configured ? (
                    <p className="text-xs text-muted-foreground/80 font-mono">
                      VirusTotal / Safe Browsing not configured — add API keys to enable live blocklist checks.
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap gap-2 text-xs font-mono">
                        {result.reputation.vt_status === "ok" && result.reputation.virustotal && (
                          <span className="px-2 py-0.5 border border-border">
                            VirusTotal: <span className="text-destructive">{result.reputation.virustotal.malicious}</span> mal /{" "}
                            <span className="text-[#FFD700]">{result.reputation.virustotal.suspicious}</span> susp
                          </span>
                        )}
                        {result.reputation.gsb_status === "ok" && (
                          <span className="px-2 py-0.5 border border-border">
                            Safe Browsing:{" "}
                            {result.reputation.safe_browsing?.threat_types?.length
                              ? <span className="text-destructive">{result.reputation.safe_browsing.threat_types.join(", ")}</span>
                              : <span className="text-primary">clean</span>}
                          </span>
                        )}
                      </div>
                      {result.reputation.reasons?.map((r, i) => (
                        <div key={i} className="text-xs text-foreground/80 font-mono">› {r}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {result.signals?.length > 0 && (
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Detected signals</div>
                  <ul className="space-y-1.5">
                    {result.signals.map((s, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <span className="text-destructive font-mono">›</span>
                        <span className="text-foreground/90">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.recommendations?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    <ListChecks size={13} strokeWidth={1.5} /> Recommendations
                  </div>
                  <ul className="space-y-1.5">
                    {result.recommendations.map((s, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <span className="text-primary font-mono">✓</span>
                        <span className="text-foreground/90">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="mt-6 bg-card border border-border">
          <div className="px-6 py-4 border-b border-border font-mono font-bold tracking-tight">Recent Checks</div>
          <div className="divide-y divide-border">
            {history.map((h) => {
              const s = verdictStyle(h.verdict);
              return (
                <div key={h.id} className="flex items-center justify-between gap-4 px-6 py-3">
                  <div className="min-w-0 flex items-center gap-3">
                    <span className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-widest shrink-0 ${s.color} ${s.border} ${s.bg}`}>
                      {s.label}
                    </span>
                    <span className="font-mono text-sm text-muted-foreground truncate">{h.content}</span>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground shrink-0">{timeAgo(h.timestamp)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
